/**
 * Gets a cookie value by name.
 * @param name - The name of the cookie to retrieve.
 * @returns The cookie value, or null if not found.
 */
export const getCookie = (name: string): string | null => {
    const cookies = document.cookie.split('; ').reduce((acc, cookie) => {
        const [key, value] = cookie.split('=');
        acc[key] = decodeURIComponent(value);
        return acc;
    }, {} as Record<string, string>);
    return cookies[name] || null;
};

/**
 * Sets a cookie with a specified name, value, and options.
 * @param name - The name of the cookie to set.
 * @param value - The value of the cookie to set.
 * @param options - Optional settings for the cookie (e.g., expiry, path, secure).
 */
export const setCookie = (
    name: string,
    value: string,
    options: {
        expires?: number | Date; // Number of days or a Date object
        path?: string;
        secure?: boolean;
        httpOnly?: boolean; // Note: `HttpOnly` cookies cannot be set via JavaScript
        sameSite?: 'Strict' | 'Lax' | 'None';
    } = {}
): void => {
    let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

    if (options.expires) {
        const expires =
            typeof options.expires === 'number'
                ? new Date(Date.now() + options.expires * 24 * 60 * 60 * 1000)
                : options.expires;
        cookieString += `; Expires=${expires.toUTCString()}`;
    }

    if (options.path) {
        cookieString += `; Path=${options.path}`;
    }

    if (options.secure) {
        cookieString += `; Secure`;
    }

    if (options.sameSite) {
        cookieString += `; SameSite=${options.sameSite}`;
    }

    // Note: `HttpOnly` cookies cannot be set via JavaScript, only via server-side headers.

    document.cookie = cookieString;
};

/**
 * Deletes a cookie by name.
 * @param name - The name of the cookie to delete.
 * @param path - The path where the cookie is accessible (optional).
 */
export const destroyCookie = (name: string, path: string = '/'): void => {
    document.cookie = `${encodeURIComponent(name)}=; Max-Age=0; Path=${path};`;
};